import { Link as ReachLink } from "react-router-dom";
import {
    Box,
    chakra,
    Flex,
    Image,
    Tag,
    Text,
    Link,
    VStack,
    HStack,
} from '@chakra-ui/react';
import { useLanguage } from '../i18n/LanguageContext';
import { getLocalizedMember } from '../i18n/translations';
import ReviewWarning from './ReviewWarning';

interface MemberCardProps {
    name_ch: string,
    name_en: string,
    title: string,
    email: string,
    expertise: string,
    website: string,
    avatar: String,
}

function MemberCard(props: MemberCardProps) {
    const { language, t } = useLanguage();
    const displayMember = getLocalizedMember(props, language);
    const { name_ch, name_en, title, email, expertise, website, avatar, warning } = displayMember;
    const { index, group } = props;
    return (
        <Link
            as={ReachLink}
            to="/personalPage"
            state={{ index: index, group: group }}
            color='gray'
        >
            {/* RWD: md 以上 */}
            <Flex
                mx="auto"
                w="90%"
                mt={12}
                bg="white"
                shadow="lg"
                rounded="lg"
                overflow="hidden"
                display={{ base: 'none', md: 'flex' }}
            >
                <Flex
                    w={1 / 3}
                >
                    <Image width='70%' src={avatar} alt='' />
                </Flex>

                <Box
                    w={2 / 3}
                    p={{
                        textAlign: 'left',
                        overflowX: 'overlay',
                    }}
                    padding='3'
                >
                    <chakra.h1
                        fontSize="2xl"
                        fontWeight="bold"
                        color="gray.800"
                    >
                        {name_ch}
                    </chakra.h1>
                    <chakra.h2
                        fontSize="xl"
                        fontWeight="bold"
                        color="gray.800"
                    >
                        {name_en}
                    </chakra.h2>

                    <chakra.p
                        mt={3}
                        fontSize="sm"
                        color="gray.600"
                    >

                        <HStack>
                            <Tag size={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.title')}
                            </Tag>
                            <Text fontSize='lg'>{title}<ReviewWarning message={warning} /></Text>
                        </HStack>
                        <HStack mt={2}>
                            <Tag size={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.email')}
                            </Tag>
                            <Text fontSize='lg'>{email}</Text>
                        </HStack>
                        <HStack mt={2}>
                            <Tag size={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.expertise')}
                            </Tag>
                            <Text fontSize='lg'>{expertise}</Text>
                        </HStack>
                        <HStack mt={2}>
                            <Tag size={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.website')}
                            </Tag>
                            <Link fontSize='lg' href={website} isExternal>{website}</Link>
                        </HStack>
                    </chakra.p>
                </Box>
            </Flex>
            
            {/* RWD: md 以下 */}
            <VStack
                mx="auto"
                mt={12}
                bg="white"
                shadow="lg"
                rounded="lg"
                overflow="hidden"
                display={{ md: 'none' }}
            >
                <Image width='100%' src={avatar} alt='' />
                <Box
                    p={{
                        textAlign: 'left',
                        overflowX: 'overlay',
                    }}
                    padding='3'
                >
                    <chakra.h1
                        fontSize="2xl"
                        fontWeight="bold"
                        color="gray.800"
                        _dark={{
                            color: "white",
                        }}
                    >
                        {name_ch}
                    </chakra.h1>
                    <chakra.h2
                        fontSize="xl"
                        fontWeight="bold"
                        color="gray.800"
                        _dark={{
                            color: "white",
                        }}
                    >
                        {name_en}
                    </chakra.h2>

                    <chakra.p
                        mt={3}
                        fontSize="sm"
                        color="gray.600"
                        _dark={{
                            color: "gray.400",
                        }}
                    >
                        <HStack>
                            <Tag size={'lg'} key={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.title')}
                            </Tag>
                            <Text fontSize='lg'>{title}<ReviewWarning message={warning} /></Text>
                        </HStack>
                        <HStack mt={2}>
                            <Tag size={'lg'} key={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.email')}
                            </Tag>
                            <Text fontSize='lg'>{email}</Text>
                        </HStack>
                        <HStack mt={2}>
                            <Tag size={'lg'} key={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.expertise')}
                            </Tag>
                            <Text fontSize='lg'>{expertise}</Text>
                        </HStack>
                        <HStack mt={2}>
                            <Tag size={'lg'} key={'lg'} variant='solid' colorScheme='teal'>
                                {t('common.website')}
                            </Tag>
                            <Link fontSize='lg' href={website} isExternal>{website}</Link>
                        </HStack>
                    </chakra.p>
                </Box>
            </VStack>
        </Link>
    );
}

function SimpleMemberCard(props) {
  const { language, t } = useLanguage();
  const displayMember = getLocalizedMember(props, language);
  const { name_ch, name_en, title, email, expertise, website, avatar, warning } = displayMember;
  const { index, group } = props;

  // Helper component for rendering label + value
  const Field = ({ label, children, mt }) => (
    <HStack mt={mt} spacing={2}>
      <Tag size="lg" variant="solid" colorScheme="teal">
        {label}
      </Tag>
      {children}
    </HStack>
  );

  // Prepare list of fields
  const fields = [
    { label: t('common.title'), value: title, render: val => <Text fontSize="lg">{val}<ReviewWarning message={warning} /></Text> },
    { label: t('common.email'), value: email, render: val => <Text fontSize="lg">{val}</Text> },
    { label: t('common.expertise'), value: expertise, render: val => <Text fontSize="lg">{val}</Text> },
    { label: t('common.website'), value: website, render: val => (
      <Link fontSize="lg" href={val} isExternal>
        {val}
      </Link>
    ) },
  ];

  return (
    <Link
      as={ReachLink}
      to="/personalPage"
      state={{ index, group }}
      color="gray"
    >
      {/* RWD: md 以上 */}
      <Flex
        mx="auto"
        w="90%"
        mt={12}
        bg="white"
        shadow="lg"
        rounded="lg"
        overflow="hidden"
        display={{ base: 'none', md: 'flex' }}
      >
        <Flex w={1 / 3}>
          <Image width="70%" src={avatar} alt={`${name_en} avatar`} />
        </Flex>

        <Box w={2 / 3} p={3} textAlign="left">
          <chakra.h1 fontSize="2xl" fontWeight="bold" color="gray.800">
            {name_ch}
          </chakra.h1>
          <chakra.h2 fontSize="xl" fontWeight="bold" color="gray.800">
            {name_en}
          </chakra.h2>

          <chakra.p mt={3} fontSize="sm" color="gray.600">
            {fields.map(
              (field, idx) =>
                field.value && (
                  <Field key={field.label} label={field.label} mt={idx === 0 ? 0 : 2}>
                    {field.render(field.value)}
                  </Field>
                )
            )}
          </chakra.p>
        </Box>
      </Flex>

      {/* RWD: md 以下 */}
      <VStack
        mx="auto"
        mt={12}
        bg="white"
        shadow="lg"
        rounded="lg"
        overflow="hidden"
        display={{ md: 'none' }}
      >
        <Image width="100%" src={avatar} alt={`${name_en} avatar`} />
        <Box
            w={2 / 3}
            p={{
                textAlign: 'left',
                overflowX: 'overlay',
            }}
            padding='3'
        >
          <chakra.h1
            fontSize="2xl"
            fontWeight="bold"
            color="gray.800"
            _dark={{
                color: "white",
            }}
        >
            {name_ch}
        </chakra.h1>
        <chakra.h2
            fontSize="xl"
            fontWeight="bold"
            color="gray.800"
            _dark={{
                color: "white",
            }}
        >
            {name_en}
        </chakra.h2>

          <chakra.p mt={3} fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
            {fields.map(
              (field, idx) =>
                field.value && (
                  <Field key={field.label} label={field.label} mt={idx === 0 ? 0 : 2}>
                    {field.render(field.value)}
                  </Field>
                )
            )}
          </chakra.p>
        </Box>
      </VStack>
    </Link>
  );
}


export { MemberCard, SimpleMemberCard };
